import type { ModelDefinition } from "@/models.js";

/**
 * Prebuilt voices supported by the Gemini text-to-speech models. The first
 * entry ("Kore") is used as the default when the caller omits `voice`.
 */
const GEMINI_TTS_VOICES = [
	"Kore",
	"Puck",
	"Zephyr",
	"Charon",
	"Fenrir",
	"Leda",
	"Orus",
	"Aoede",
	"Callirrhoe",
	"Autonoe",
	"Enceladus",
	"Iapetus",
	"Umbriel",
	"Algieba",
	"Despina",
	"Erinome",
	"Algenib",
	"Rasalgethi",
	"Laomedeia",
	"Achernar",
	"Alnilam",
	"Schedar",
	"Gacrux",
	"Pulcherrima",
	"Achird",
	"Zubenelgenubi",
	"Vindemiatrix",
	"Sadachbia",
	"Sadaltager",
	"Sulafat",
];

export const googleModels = [
	{
		id: "gemini-2.5-pro",
		name: "Gemini 2.5 Pro",
		description:
			"Google's most capable model with reasoning and multimodal support.",
		family: "google",
		releasedAt: new Date("2025-03-25"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-pro",
				serviceTiers: ["flex", "priority"],
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				cachedInputPrice: "0.125e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "1.25e-6",
						outputPrice: "10.0e-6",
						cachedInputPrice: "0.125e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "2.5e-6",
						outputPrice: "15.0e-6",
						cachedInputPrice: "0.25e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.035", // $35 per 1000 prompts
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsN: true,
				supportsNStreaming: false,
				maxN: 8,
				deactivatedAt: new Date("2026-10-16"),
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-2.5-pro",
				serviceTiers: ["priority"],
				serviceTierRegions: ["global"],
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				cachedInputPrice: "0.125e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "1.25e-6",
						outputPrice: "10.0e-6",
						cachedInputPrice: "0.125e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "2.5e-6",
						outputPrice: "15.0e-6",
						cachedInputPrice: "0.25e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.035", // $35 per 1000 prompts
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsN: true,
				supportsNStreaming: false,
				maxN: 8,
				deactivatedAt: new Date("2026-10-16"),
			},
		],
	},
	{
		id: "gemini-2.5-pro-preview-05-06",
		name: "Gemini 2.5 Pro Preview (05-06)",
		description: "Preview version of Gemini 2.5 Pro (May 2025).",
		family: "google",
		releasedAt: new Date("2025-05-06"),
		providers: [
			{
				test: "skip",
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-pro-preview-05-06",
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "1.25e-6",
						outputPrice: "10.0e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "2.5e-6",
						outputPrice: "15.0e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-07-15"),
			},
			{
				test: "skip",
				providerId: "google-vertex",
				externalId: "gemini-2.5-pro-preview-05-06",
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "1.25e-6",
						outputPrice: "10.0e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "2.5e-6",
						outputPrice: "15.0e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-07-15"),
			},
		],
	},
	{
		id: "gemini-2.5-pro-preview-06-05",
		name: "Gemini 2.5 Pro Preview (06-05)",
		description: "Preview version of Gemini 2.5 Pro (June 2025).",
		family: "google",
		releasedAt: new Date("2025-06-05"),
		providers: [
			{
				test: "skip",
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-pro-preview-06-05",
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "1.25e-6",
						outputPrice: "10.0e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "2.5e-6",
						outputPrice: "15.0e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-07-15"),
			},
			{
				test: "skip",
				providerId: "google-vertex",
				externalId: "gemini-2.5-pro-preview-06-05",
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "1.25e-6",
						outputPrice: "10.0e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "2.5e-6",
						outputPrice: "15.0e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-07-15"),
			},
		],
	},
	{
		id: "gemini-2.5-flash-preview-04-17",
		name: "Gemini 2.5 Flash Preview (04-17)",
		description: "Early preview of fast Gemini 2.5 Flash model.",
		family: "google",
		releasedAt: new Date("2025-04-17"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-flash-preview-04-17",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-07-15"),
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-2.5-flash-preview-04-17",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-07-15"),
			},
		],
	},
	{
		id: "gemini-2.5-flash-preview-05-20",
		name: "Gemini 2.5 Flash Preview (05-20)",
		description: "May preview of Gemini 2.5 Flash with improved performance.",
		family: "google",
		releasedAt: new Date("2025-05-20"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-flash-preview-05-20",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-07-15"),
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-2.5-flash-preview-05-20",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-07-15"),
			},
		],
	},
	{
		id: "gemini-2.5-flash-preview-09-2025",
		name: "Gemini 2.5 Flash Preview (09-2025)",
		description: "September preview of Gemini 2.5 Flash with caching.",
		family: "google",
		releasedAt: new Date("2025-09-25"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-flash-preview-09-2025",
				deactivatedAt: new Date("2026-01-17"),
				inputPrice: "0.3e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.03e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "google-vertex",
				deactivatedAt: new Date("2026-01-27"),
				externalId: "gemini-2.5-flash-preview-09-2025",
				inputPrice: "0.3e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.03e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-2.5-flash",
		name: "Gemini 2.5 Flash",
		description: "Fast, cost-effective model with reasoning capabilities.",
		family: "google",
		releasedAt: new Date("2025-08-26"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-flash",
				serviceTiers: ["flex", "priority"],
				inputPrice: "0.3e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.03e-6",
				inputAudioPrice: "1.0e-6",
				cachedInputAudioPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65535,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high"],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.035", // $35 per 1000 prompts
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsN: true,
				supportsNStreaming: false,
				maxN: 8,
				deactivatedAt: new Date("2026-10-16"),
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-2.5-flash",
				serviceTiers: ["priority"],
				serviceTierRegions: ["global"],
				inputPrice: "0.3e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.03e-6",
				inputAudioPrice: "1.0e-6",
				cachedInputAudioPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65535,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.035", // $35 per 1000 prompts
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsN: true,
				supportsNStreaming: false,
				maxN: 8,
				deactivatedAt: new Date("2026-10-16"),
			},
		],
	},
	{
		id: "gemini-2.5-flash-lite",
		name: "Gemini 2.5 Flash Lite",
		description:
			"Ultra-lightweight Gemini for high-volume, cost-sensitive tasks.",
		family: "google",
		releasedAt: new Date("2025-07-22"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-flash-lite",
				serviceTiers: ["flex", "priority"],
				inputPrice: "0.1e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.01e-6",
				inputAudioPrice: "0.3e-6",
				cachedInputAudioPrice: "0.03e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65535,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsN: true,
				supportsNStreaming: false,
				maxN: 8,
				deactivatedAt: new Date("2026-10-16"),
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-2.5-flash-lite",
				serviceTiers: ["priority"],
				serviceTierRegions: ["global"],
				inputPrice: "0.1e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.01e-6",
				inputAudioPrice: "0.3e-6",
				cachedInputAudioPrice: "0.03e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65535,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsN: true,
				supportsNStreaming: false,
				maxN: 8,
				deactivatedAt: new Date("2026-10-16"),
			},
		],
	},
	{
		id: "gemini-2.5-flash-lite-preview-09-2025",
		name: "Gemini 2.5 Flash Lite Preview (09-2025)",
		description: "Preview of lightweight Gemini Flash Lite variant.",
		family: "google",
		releasedAt: new Date("2025-09-25"),
		providers: [
			{
				providerId: "google-ai-studio",
				deactivatedAt: new Date("2026-03-31"),
				externalId: "gemini-2.5-flash-lite-preview-09-2025",
				inputPrice: "0.1e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.01e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65535,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "google-vertex",
				deactivatedAt: new Date("2026-07-09"),
				externalId: "gemini-2.5-flash-lite-preview-09-2025",
				inputPrice: "0.1e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.01e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65535,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsN: true,
				supportsNStreaming: false,
				maxN: 8,
			},
		],
	},
	{
		id: "gemini-pro-latest",
		name: "Gemini Pro Latest",
		description: "Always points to the latest Gemini Pro model.",
		family: "google",
		releasedAt: new Date("2026-02-27"),
		providers: [
			{
				test: "skip",
				providerId: "google-ai-studio",
				externalId: "gemini-pro-latest",
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2e-6",
						outputPrice: "12e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4e-6",
						outputPrice: "18e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.014",
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-3-pro-preview",
		name: "Gemini 3 Pro (Preview)",
		description:
			"Preview of next-generation Gemini 3 Pro with enhanced capabilities.",
		family: "google",
		releasedAt: new Date("2025-11-18"),
		providers: [
			{
				test: "skip",
				providerId: "google-ai-studio",
				externalId: "gemini-3-pro-preview",
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2e-6",
						outputPrice: "12e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4e-6",
						outputPrice: "18e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.014", // $14 per 1000 queries for Gemini 3
				jsonOutput: true,
				jsonOutputSchema: true,
				deprecatedAt: new Date("2026-02-27"),
				deactivatedAt: new Date("2026-03-26"),
			},
			{
				test: "skip",
				providerId: "google-vertex",
				externalId: "gemini-3-pro-preview",
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2e-6",
						outputPrice: "12e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4e-6",
						outputPrice: "18e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.014", // $14 per 1000 queries for Gemini 3
				jsonOutput: true,
				jsonOutputSchema: true,
				deprecatedAt: new Date("2026-02-27"),
				deactivatedAt: new Date("2026-03-26"),
			},
		],
	},
	{
		id: "gemini-3.1-pro-preview",
		name: "Gemini 3.1 Pro (Preview)",
		description:
			"Preview of Gemini 3.1 Pro with enhanced capabilities and reasoning.",
		family: "google",
		releasedAt: new Date("2026-02-19"),
		providers: [
			{
				test: "skip",
				providerId: "google-ai-studio",
				externalId: "gemini-3.1-pro-preview",
				serviceTiers: ["flex", "priority"],
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2e-6",
						outputPrice: "12e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4e-6",
						outputPrice: "18e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.014",
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				test: "skip",
				providerId: "iceberg",
				externalId: "gemini-3.1-pro-preview",
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2e-6",
						outputPrice: "12e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4e-6",
						outputPrice: "18e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.014",
				jsonOutput: true,
				jsonOutputSchema: true,
				// In plain json_object mode the model leaks its deliberation after the
				// JSON object ("Wait, no, the prompt says …"), both streaming and
				// non-streaming — json_schema mode is unaffected
				// (verified live 2026-07-30)
				healStreamingJsonOutput: true,
			},
			{
				test: "skip",
				providerId: "google-vertex",
				externalId: "gemini-3.1-pro-preview",
				serviceTiers: ["flex", "priority"],
				serviceTierRegions: ["global"],
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2e-6",
						outputPrice: "12e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4e-6",
						outputPrice: "18e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.014",
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				test: "skip",
				providerId: "quartz",
				externalId: "gemini-3.1-pro-preview",
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2e-6",
						outputPrice: "12e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4e-6",
						outputPrice: "18e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.014",
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-3.1-flash-lite-preview",
		name: "Gemini 3.1 Flash Lite (Preview)",
		description:
			"Preview of ultra-lightweight Gemini 3.1 Flash Lite for high-volume, cost-sensitive tasks.",
		family: "google",
		releasedAt: new Date("2026-03-03"),
		providers: [
			{
				test: "skip",
				providerId: "google-ai-studio",
				externalId: "gemini-3.1-flash-lite-preview",
				deprecatedAt: new Date("2026-05-12"),
				deactivatedAt: new Date("2026-05-25"),
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				cachedInputPrice: "0.025e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				reasoning: true,
				reasoningMaxTokens: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				test: "skip",
				providerId: "google-vertex",
				externalId: "gemini-3.1-flash-lite-preview",
				deprecatedAt: new Date("2026-05-12"),
				deactivatedAt: new Date("2026-05-25"),
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				cachedInputPrice: "0.025e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				reasoning: true,
				reasoningMaxTokens: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-3.1-flash-lite",
		name: "Gemini 3.1 Flash Lite",
		description:
			"Ultra-lightweight Gemini 3.1 Flash Lite for high-volume, cost-sensitive tasks.",
		family: "google",
		releasedAt: new Date("2026-05-08"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-3.1-flash-lite",
				serviceTiers: ["flex", "priority"],
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				cachedInputPrice: "0.025e-6",
				cacheWriteInputPrice: "0.08333e-6",
				inputAudioPrice: "0.5e-6",
				cachedInputAudioPrice: "0.05e-6",
				requestPrice: "0",
				webSearchPrice: "0.014",
				contextSize: 1048576,
				maxOutput: 65536,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-3.1-flash-lite",
				serviceTiers: ["flex", "priority"],
				serviceTierRegions: ["global"],
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				cachedInputPrice: "0.025e-6",
				cacheWriteInputPrice: "0.08333e-6",
				inputAudioPrice: "0.5e-6",
				cachedInputAudioPrice: "0.05e-6",
				requestPrice: "0",
				webSearchPrice: "0.014",
				contextSize: 1048576,
				maxOutput: 65536,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				webSearch: true,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-3.1-flash-lite-image",
		name: "Gemini 3.1 Flash Lite Image",
		description:
			"Our smallest and most cost effective image generation and editing model, built for at scale usage.",
		family: "google",
		output: ["text", "image"],
		releasedAt: new Date("2026-06-23"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-3.1-flash-lite-image",
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				imageInputPrice: "0.25e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "30e-6",
				imageOutputTokensByResolution: {
					"1K": 1120,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 4096,
				streaming: true,
				vision: true,
				tools: false,
				reasoning: true,
				reasoningEfforts: ["minimal", "high"],
				reasoningMaxTokens: true,
				// upstream rejects responseMimeType application/json (400)
				jsonOutput: false,
				jsonOutputSchema: false,
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-3.1-flash-lite-image",
				serviceTiers: ["flex"],
				serviceTierRegions: ["global"],
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				imageInputPrice: "0.25e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "30e-6",
				imageOutputTokensByResolution: {
					"1K": 1120,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 4096,
				streaming: true,
				vision: true,
				tools: false,
				reasoning: true,
				reasoningMaxTokens: true,
				// Vertex serves this model with HIGH thinking only; other thinking
				// levels are rejected with a 400
				reasoningEfforts: ["none", "high"],
				// upstream rejects responseMimeType application/json (400)
				jsonOutput: false,
				jsonOutputSchema: false,
			},
		],
	},
	{
		id: "gemini-3.5-flash",
		name: "Gemini 3.5 Flash",
		description:
			"Gemini 3.5 Flash combines frontier intelligence with superior speed, search, and grounding.",
		family: "google",
		releasedAt: new Date("2026-05-19"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-3.5-flash",
				serviceTiers: ["flex", "priority"],
				inputPrice: "1.5e-6",
				outputPrice: "9e-6",
				cachedInputPrice: "0.15e-6",
				cacheWriteInputPrice: "0.08333e-6",
				inputAudioPrice: "3e-6",
				cachedInputAudioPrice: "0.3e-6",
				requestPrice: "0",
				webSearchPrice: "0.014",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				// AI Studio's json_object mode emits a stray trailing brace (and
				// sometimes prose) after the JSON — Vertex is unaffected
				// (verified live 2026-07-14)
				healStreamingJsonOutput: true,
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-3.5-flash",
				serviceTiers: ["flex", "priority"],
				serviceTierRegions: ["global"],
				inputPrice: "1.5e-6",
				outputPrice: "9e-6",
				cachedInputPrice: "0.15e-6",
				cacheWriteInputPrice: "0.08333e-6",
				inputAudioPrice: "3e-6",
				cachedInputAudioPrice: "0.3e-6",
				requestPrice: "0",
				webSearchPrice: "0.014",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				webSearch: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-3.5-flash-lite",
		name: "Gemini 3.5 Flash Lite",
		description:
			"Gemini 3.5 Flash Lite is the fastest, most cost-effective 3.5 model for high-throughput, low-latency tasks like agentic search and document processing.",
		family: "google",
		releasedAt: new Date("2026-07-21"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-3.5-flash-lite",
				serviceTiers: ["flex", "priority"],
				inputPrice: "0.3e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.03e-6",
				cacheWriteInputPrice: "0.08333e-6",
				requestPrice: "0",
				webSearchPrice: "0.014",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-3.5-flash-lite",
				serviceTiers: ["flex", "priority"],
				serviceTierRegions: ["global"],
				inputPrice: "0.3e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.03e-6",
				cacheWriteInputPrice: "0.08333e-6",
				requestPrice: "0",
				webSearchPrice: "0.014",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				webSearch: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-3.6-flash",
		name: "Gemini 3.6 Flash",
		description:
			"Gemini 3.6 Flash delivers better coding, knowledge work, and multimodal performance than 3.5 Flash while using fewer output tokens at a lower price.",
		family: "google",
		releasedAt: new Date("2026-07-21"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-3.6-flash",
				serviceTiers: ["flex", "priority"],
				inputPrice: "1.5e-6",
				outputPrice: "7.5e-6",
				cachedInputPrice: "0.15e-6",
				cacheWriteInputPrice: "0.08333e-6",
				requestPrice: "0",
				webSearchPrice: "0.014",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "iceberg",
				externalId: "gemini-3.6-flash",
				inputPrice: "1.5e-6",
				outputPrice: "7.5e-6",
				cachedInputPrice: "0.15e-6",
				cacheWriteInputPrice: "0.08333e-6",
				requestPrice: "0",
				webSearchPrice: "0.014",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-3.6-flash",
				serviceTiers: ["flex", "priority"],
				serviceTierRegions: ["global"],
				inputPrice: "1.5e-6",
				outputPrice: "7.5e-6",
				cachedInputPrice: "0.15e-6",
				cacheWriteInputPrice: "0.08333e-6",
				requestPrice: "0",
				webSearchPrice: "0.014",
				contextSize: 1048576,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				webSearch: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "veo-3.1-generate-preview",
		name: "Veo 3.1",
		description: "Google Veo 3.1 video generation with audio.",
		family: "google",
		output: ["video"],
		maxVideoDurationSeconds: 10,
		stability: "beta",
		releasedAt: new Date("2026-03-14"),
		providers: [
			{
				providerId: "google-vertex",
				externalId: "veo-3.1-generate-001",
				inputPrice: undefined,
				outputPrice: undefined,
				requestPrice: undefined,
				perSecondPrice: {
					default_audio: "0.4",
					default_video: "0.2",
					"4k_audio": "0.6",
					"4k_video": "0.4",
				},
				supportedVideoSizes: [
					"1280x720",
					"720x1280",
					"1920x1080",
					"1080x1920",
					"3840x2160",
					"2160x3840",
				],
				supportedVideoDurationsSeconds: [4, 6, 8, 10],
				supportedVideoDurationsSecondsImageToVideo: [4, 6, 8],
				contextSize: 32768,
				maxOutput: 1,
				streaming: false,
				tools: false,
				jsonOutput: false,
				jsonOutputSchema: false,
				videoGenerations: true,
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: true,
			},
			{
				providerId: "avalanche",
				externalId: "veo3",
				inputPrice: undefined,
				outputPrice: undefined,
				requestPrice: undefined,
				perSecondPrice: {
					default_audio: "0.4",
					default_video: "0.2",
					"4k_audio": "0.6",
					"4k_video": "0.4",
				},
				supportedVideoSizes: [
					"1920x1080",
					"1080x1920",
					"3840x2160",
					"2160x3840",
				],
				supportedVideoDurationsSeconds: [8],
				contextSize: 32768,
				maxOutput: 1,
				streaming: false,
				tools: false,
				jsonOutput: false,
				jsonOutputSchema: false,
				videoGenerations: true,
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
		],
	},
	{
		id: "veo-3.1-fast-generate-preview",
		name: "Veo 3.1 Fast",
		description: "Google Veo 3.1 Fast video generation with audio.",
		family: "google",
		output: ["video"],
		maxVideoDurationSeconds: 10,
		stability: "beta",
		releasedAt: new Date("2026-03-14"),
		providers: [
			{
				providerId: "google-vertex",
				externalId: "veo-3.1-fast-generate-001",
				inputPrice: undefined,
				outputPrice: undefined,
				requestPrice: undefined,
				perSecondPrice: {
					default_audio: "0.15",
					default_video: "0.1",
					"4k_audio": "0.35",
					"4k_video": "0.3",
				},
				supportedVideoSizes: [
					"1280x720",
					"720x1280",
					"1920x1080",
					"1080x1920",
					"3840x2160",
					"2160x3840",
				],
				supportedVideoDurationsSeconds: [4, 6, 8, 10],
				supportedVideoDurationsSecondsImageToVideo: [4, 6, 8],
				contextSize: 32768,
				maxOutput: 1,
				streaming: false,
				tools: false,
				jsonOutput: false,
				jsonOutputSchema: false,
				videoGenerations: true,
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: true,
			},
			{
				providerId: "avalanche",
				externalId: "veo3_fast",
				inputPrice: undefined,
				outputPrice: undefined,
				requestPrice: undefined,
				perSecondPrice: {
					default: "0.15",
					"4k": "0.35",
				},
				supportedVideoSizes: [
					"1920x1080",
					"1080x1920",
					"3840x2160",
					"2160x3840",
				],
				supportedVideoDurationsSeconds: [8],
				contextSize: 32768,
				maxOutput: 1,
				streaming: false,
				tools: false,
				jsonOutput: false,
				jsonOutputSchema: false,
				videoGenerations: true,
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
		],
	},
	{
		id: "gemini-3.1-flash-image-preview",
		name: "Gemini 3.1 Flash Image (Preview)",
		description:
			"Preview of Gemini 3.1 Flash with native image generation capabilities.",
		family: "google",
		output: ["text", "image"],
		releasedAt: new Date("2026-02-26"),
		providers: [
			{
				test: "skip",
				providerId: "google-ai-studio",
				externalId: "gemini-3.1-flash-image-preview",
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				imageInputPrice: "0.25e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "60e-6",
				imageOutputTokensByResolution: {
					"0.5K": 747,
					"1K": 1120,
					"2K": 1680,
					"4K": 2520,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 65536,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				test: "skip",
				providerId: "glacier",
				externalId: "gemini-3.1-flash-image-preview",
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				imageInputPrice: "0.25e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "60e-6",
				imageOutputTokensByResolution: {
					"0.5K": 747,
					"1K": 1120,
					"2K": 1680,
					"4K": 2520,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 65536,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				test: "skip",
				providerId: "google-vertex",
				externalId: "gemini-3.1-flash-image-preview",
				serviceTiers: ["flex"],
				serviceTierRegions: ["global"],
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				imageInputPrice: "0.25e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "60e-6",
				imageOutputTokensByResolution: {
					"0.5K": 747,
					"1K": 1120,
					"2K": 1680,
					"4K": 2520,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 65536,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				test: "skip",
				providerId: "quartz",
				externalId: "gemini-3.1-flash-image-preview",
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				imageInputPrice: "0.25e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "60e-6",
				imageOutputTokensByResolution: {
					"0.5K": 747,
					"1K": 1120,
					"2K": 1680,
					"4K": 2520,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 65536,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-3-flash-preview",
		name: "Gemini 3 Flash (Preview)",
		description:
			"Preview of next-generation Gemini 3 Flash built for speed, combining frontier intelligence with superior search and grounding.",
		family: "google",
		releasedAt: new Date("2025-12-17"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-3-flash-preview",
				serviceTiers: ["flex", "priority"],
				inputPrice: "0.5e-6",
				outputPrice: "3e-6",
				cachedInputPrice: "0.05e-6",
				inputAudioPrice: "1.0e-6",
				cachedInputAudioPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65535,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.014", // $14 per 1000 queries for Gemini 3
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "iceberg",
				externalId: "gemini-3-flash-preview",
				inputPrice: "0.5e-6",
				outputPrice: "3e-6",
				cachedInputPrice: "0.05e-6",
				inputAudioPrice: "1.0e-6",
				cachedInputAudioPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65535,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.014", // $14 per 1000 queries for Gemini 3
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-3-flash-preview",
				serviceTiers: ["flex", "priority"],
				serviceTierRegions: ["global"],
				inputPrice: "0.5e-6",
				outputPrice: "3e-6",
				cachedInputPrice: "0.05e-6",
				inputAudioPrice: "1.0e-6",
				cachedInputAudioPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65535,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.014", // $14 per 1000 queries for Gemini 3
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-2.5-flash-image-preview",
		name: "Gemini 2.5 Flash Image (Preview)",
		description: "Gemini Flash with native image generation capabilities.",
		aliases: ["nano banana"],
		family: "google",
		output: ["text", "image"],
		releasedAt: new Date("2025-10-02"),
		providers: [
			{
				test: "skip",
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-flash-image-preview",
				inputPrice: "0.3e-6",
				outputPrice: "2.5e-6",
				imageOutputPrice: "30e-6",
				imageOutputTokensByResolution: {
					"1K": 1120,
					"2K": 1120,
					"4K": 2000,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
				deprecatedAt: new Date("2025-12-04"),
				deactivatedAt: new Date("2026-01-15"),
			},
			{
				test: "skip",
				providerId: "google-vertex",
				externalId: "gemini-2.5-flash-image-preview",
				inputPrice: "0.3e-6",
				outputPrice: "2.5e-6",
				imageOutputPrice: "30e-6",
				imageOutputTokensByResolution: {
					"1K": 1120,
					"2K": 1120,
					"4K": 2000,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
				deprecatedAt: new Date("2025-12-04"),
				deactivatedAt: new Date("2026-01-15"),
			},
		],
	},
	{
		id: "gemini-2.5-flash-image",
		name: "Gemini 2.5 Flash Image",
		description: "Production Gemini Flash model for text and image generation.",
		aliases: ["nano banana"],
		family: "google",
		output: ["text", "image"],
		releasedAt: new Date("2025-10-02"),
		providers: [
			{
				test: "skip",
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-flash-image",
				serviceTiers: ["flex", "priority"],
				inputPrice: "0.3e-6",
				outputPrice: "30e-6",
				cachedInputPrice: "0.03e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2026-10-02"),
			},
			{
				test: "skip",
				providerId: "google-vertex",
				externalId: "gemini-2.5-flash-image",
				inputPrice: "0.3e-6",
				outputPrice: "30e-6",
				cachedInputPrice: "0.03e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2026-10-02"),
			},
			{
				test: "skip",
				providerId: "glacier",
				externalId: "gemini-2.5-flash-image",
				inputPrice: "0.3e-6",
				outputPrice: "30e-6",
				cachedInputPrice: "0.03e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-3-pro-image-preview",
		name: "Gemini 3 Pro Image (Preview)",
		description:
			"Native image generation model optimized for speed, flexibility, and contextual understanding. Text priced same as Gemini 3 Pro, image output at $0.134/image (1K-2K) or $0.24/image (4K).",
		aliases: ["nano banana", "nano banana 2"],
		family: "google",
		output: ["text", "image"],
		releasedAt: new Date("2025-11-20"),
		providers: [
			{
				test: "skip",
				providerId: "google-ai-studio",
				externalId: "gemini-3-pro-image-preview",
				serviceTiers: ["flex"],
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				imageInputPrice: "2e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "120e-6",
				imageOutputTokensByResolution: {
					"1K": 1120,
					"2K": 1120,
					"4K": 2000,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				test: "skip",
				providerId: "glacier",
				externalId: "gemini-3-pro-image-preview",
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				imageInputPrice: "2e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "120e-6",
				imageOutputTokensByResolution: {
					"1K": 1120,
					"2K": 1120,
					"4K": 2000,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				test: "skip",
				providerId: "iceberg",
				externalId: "gemini-3-pro-image-preview",
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				imageInputPrice: "2e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "120e-6",
				imageOutputTokensByResolution: {
					"1K": 1120,
					"2K": 1120,
					"4K": 2000,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				test: "skip",
				providerId: "google-vertex",
				externalId: "gemini-3-pro-image-preview",
				serviceTiers: ["flex"],
				serviceTierRegions: ["global"],
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				imageInputPrice: "2e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "120e-6",
				imageOutputTokensByResolution: {
					"1K": 1120,
					"2K": 1120,
					"4K": 2000,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				test: "skip",
				providerId: "quartz",
				externalId: "gemini-3-pro-image-preview",
				inputPrice: "2e-6",
				outputPrice: "12e-6",
				cachedInputPrice: "0.2e-6",
				imageInputPrice: "2e-6",
				imageInputTokensByResolution: { default: 560 },
				imageOutputPrice: "120e-6",
				imageOutputTokensByResolution: {
					"1K": 1120,
					"2K": 1120,
					"4K": 2000,
					default: 1120,
				},
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "gemini-2.5-flash-preview-04-17-thinking",
		name: "Gemini 2.5 Flash Preview Thinking (04-17)",
		description: "Gemini Flash preview with explicit thinking/reasoning mode.",
		family: "google",
		releasedAt: new Date("2025-04-17"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-flash-preview-04-17-thinking",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-07-22"),
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-2.5-flash-preview-04-17-thinking",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-07-22"),
			},
		],
	},
	{
		id: "gemini-1.5-pro",
		name: "Gemini 1.5 Pro",
		description: "Previous generation Gemini Pro with multimodal support.",
		family: "google",
		releasedAt: new Date("2024-05-23"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-1.5-pro",
				inputPrice: "2.5e-6",
				outputPrice: "10.0e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-09-20"),
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-1.5-pro",
				inputPrice: "2.5e-6",
				outputPrice: "10.0e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-09-20"),
			},
		],
	},
	{
		id: "gemini-1.5-flash",
		name: "Gemini 1.5 Flash",
		description: "Fast Gemini 1.5 model for quick multimodal tasks.",
		family: "google",
		releasedAt: new Date("2024-05-10"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-1.5-flash",
				inputPrice: "0.0375e-6",
				outputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-09-20"),
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-1.5-flash",
				inputPrice: "0.0375e-6",
				outputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-09-20"),
			},
		],
	},
	{
		id: "gemini-1.5-flash-8b",
		name: "Gemini 1.5 Flash 8B",
		description: "Compact 8B Gemini Flash for lightweight inference.",
		family: "google",
		releasedAt: new Date("2024-10-03"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-1.5-flash-8b",
				inputPrice: "0.0375e-6",
				outputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: false,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-09-20"),
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-1.5-flash-8b",
				inputPrice: "0.0375e-6",
				outputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: false,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2025-09-20"),
			},
		],
	},
	{
		id: "gemini-2.0-flash-lite",
		name: "Gemini 2.0 Flash Lite",
		description: "Efficient Gemini 2.0 variant for cost-effective processing.",
		family: "google",
		releasedAt: new Date("2025-02-25"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.0-flash-lite",
				inputPrice: "0.075e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deprecatedAt: new Date("2026-02-18"),
				deactivatedAt: new Date("2026-05-25"),
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-2.0-flash-lite",
				inputPrice: "0.075e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deprecatedAt: new Date("2026-02-18"),
				deactivatedAt: new Date("2026-05-25"),
			},
		],
	},
	{
		id: "gemini-2.0-flash",
		name: "Gemini 2.0 Flash",
		description: "Gemini 2.0 Flash with balanced speed and capability.",
		family: "google",
		releasedAt: new Date("2025-02-05"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.0-flash",
				inputPrice: "0.1e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.025e-6",
				inputAudioPrice: "0.7e-6",
				cachedInputAudioPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				audio: true,
				document: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deprecatedAt: new Date("2026-02-18"),
				deactivatedAt: new Date("2026-05-25"),
			},
			{
				providerId: "google-vertex",
				test: "skip",
				externalId: "gemini-2.0-flash",
				inputPrice: "0.1e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.025e-6",
				inputAudioPrice: "1.0e-6",
				cachedInputAudioPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deprecatedAt: new Date("2026-02-18"),
				deactivatedAt: new Date("2026-05-25"),
			},
		],
	},
	{
		id: "gemma-3n-e2b-it",
		name: "Gemma 3n E2B IT",
		description: "Compact Gemma 3N instruction-tuned model (2B effective).",
		family: "google",
		releasedAt: new Date("2025-06-26"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemma-3n-e2b-it",
				inputPrice: "0.075e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: false,
				deactivatedAt: new Date("2026-04-30"),
			},
		],
	},
	{
		id: "gemma-3n-e4b-it",
		name: "Gemma 3n E4B IT",
		description: "Gemma 3N instruction-tuned model (4B effective).",
		family: "google",
		releasedAt: new Date("2025-06-26"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemma-3n-e4b-it",
				inputPrice: "0.075e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: false,
				deactivatedAt: new Date("2026-04-30"),
			},
		],
	},
	{
		id: "gemma-3-1b-it",
		name: "Gemma 3 1B IT",
		description: "Tiny 1B Gemma 3 model for edge deployment.",
		family: "google",
		releasedAt: new Date("2025-03-12"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemma-3-1b-it",
				inputPrice: "0.075e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: false,
				deactivatedAt: new Date("2026-04-30"),
			},
		],
	},
	{
		id: "gemma-3-4b-it",
		name: "Gemma 3 4B IT",
		description: "Small 4B Gemma 3 model for efficient inference.",
		family: "google",
		releasedAt: new Date("2025-03-10"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemma-3-4b-it",
				inputPrice: "0.075e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: false,
				deactivatedAt: new Date("2026-04-30"),
			},
		],
	},
	{
		id: "gemma-3-12b-it",
		name: "Gemma 3 12B IT",
		description: "Medium 12B Gemma 3 model balancing size and capability.",
		family: "google",
		releasedAt: new Date("2025-03-10"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemma-3-12b-it",
				inputPrice: "0.075e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: false,
				deactivatedAt: new Date("2026-04-30"),
			},
		],
	},
	{
		id: "gemma2-9b-it",
		name: "Gemma2 9B IT",
		description: "Gemma 2 9B instruction model via Groq inference.",
		family: "google",
		releasedAt: new Date("2024-06-27"),
		providers: [
			{
				providerId: "groq",
				externalId: "gemma2-9b-it",
				inputPrice: "0.2e-6",
				outputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 8129,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				stability: "unstable",
				jsonOutput: false,
				deactivatedAt: new Date("2025-10-08"),
			},
		],
	},
	{
		id: "gemma-3-27b",
		name: "Gemma 3 27B",
		description: "Large 27B Gemma 3 model with vision support.",
		family: "google",
		releasedAt: new Date("2025-03-12"),
		providers: [
			{
				providerId: "nebius",
				externalId: "google/gemma-3-27b-it",
				inputPrice: "0.1e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 110000,
				maxOutput: undefined,
				quantization: "fp8",
				streaming: true,
				// Image input works despite the /v1/models modality claiming
				// text-only (verified 2026-07-22).
				vision: true,
				// The catalogue advertises tools, but the deployment emits
				// ```tool_code``` text instead of OpenAI tool_calls (verified
				// 2026-07-22).
				tools: false,
				jsonOutput: false,
			},
		],
	},
	{
		id: "gemma-4-31b-it",
		name: "Gemma 4 31B IT",
		description: "Large 31B Gemma 4 instruction-tuned model with reasoning.",
		family: "google",
		releasedAt: new Date("2026-04-02"),
		providers: [
			{
				providerId: "deepinfra",
				externalId: "google/gemma-4-31b-it",
				inputPrice: "0.13e-6",
				outputPrice: "0.38e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "runware",
				externalId: "google-gemma-4-31b",
				inputPrice: "0.102e-6",
				outputPrice: "0.297e-6",
				cachedInputPrice: "0.012e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				quantization: "bf16",
				streaming: true,
				reasoning: true,
				// Runware maps reasoning_effort onto its thinkingLevel setting and
				// 400s minimal/low/medium for this model.
				reasoningEfforts: ["none", "high", "xhigh", "max"],
				vision: true,
				tools: true,
				// Runware rejects json_object for this model ("Missing required
				// parameter: 'jsonSchema'") and its json_schema path hangs until the
				// upstream inference timeout, so no structured-output mode is offered.
				jsonOutput: false,
				// Runware 400s ("a conversation cannot end on an assistant turn") when
				// the last message is an assistant turn (verified 2026-07-28).
				supportsAssistantPrefill: false,
			},
			{
				providerId: "novita",
				externalId: "google/gemma-4-31b-it",
				inputPrice: "0.13e-6",
				outputPrice: "0.38e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				quantization: "bf16",
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "together-ai",
				externalId: "google/gemma-4-31b-it",
				inputPrice: "0.13e-6",
				outputPrice: "0.38e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				// Together validates reasoning_effort on this route and names the
				// accepted literals in its 400 ("Input should be 'none', 'low',
				// 'medium' or 'high'"); `none` zeroes out the reasoning output.
				reasoningEfforts: ["none", "low", "medium", "high"],
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "cerebras",
				test: "skip",
				externalId: "gemma-4-31b",
				inputPrice: "0.99e-6",
				outputPrice: "1.49e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "scx-ai",
				externalId: "gemma-4-31B-it",
				inputPrice: "0.30e-6",
				outputPrice: "0.91e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 8192,
				quantization: "bf16",
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				// RanoAI serves Gemma 4 on Furiosa RNGD NPUs. The NPU deployment
				// enforces a 20480-token window (prompt + max_tokens). Reasoning
				// arrives as `reasoning_content` (streamed as deltas) only when
				// `reasoning_effort` is passed explicitly — a request without it
				// returns no reasoning at all, and "none" suppresses it.
				// `reasoning_tokens` is reported inside completion_tokens, so
				// costs.ts lists this provider in completionIncludesReasoning.
				//
				// All four tool_choice modes are honoured, so none are declared
				// here. Prompt caching is automatic prefix caching — the first
				// request misses and later ones report `cached_tokens` — priced
				// at the `input_cache_read` rate /v1/models advertises, half the
				// input rate (verified 2026-08-05).
				providerId: "ranoai",
				externalId: "gemma-4-31b",
				deactivatedAt: new Date("2026-08-09"),
				inputPrice: "0.1e-6",
				outputPrice: "0.3e-6",
				cachedInputPrice: "0.05e-6",
				requestPrice: "0",
				contextSize: 20480,
				maxOutput: 20480,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsN: true,
			},
		],
	},
	{
		id: "gemma-4-26b-a4b-it",
		name: "Gemma 4 26B A4B IT",
		description:
			"Gemma 4 26B mixture-of-experts instruction-tuned model (4B active) with reasoning.",
		family: "google",
		releasedAt: new Date("2026-04-02"),
		providers: [
			{
				providerId: "deepinfra",
				externalId: "google/gemma-4-26b-a4b-it",
				inputPrice: "0.07e-6",
				outputPrice: "0.34e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "google/gemma-4-26b-a4b-it",
				// novita streams empty content for JSON output requests
				// (e2e JSON output streaming failures, verified 2026-07-21)
				stability: "unstable",
				inputPrice: "0.07e-6",
				outputPrice: "0.34e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				quantization: "bf16",
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gemini-embedding-001",
		name: "Gemini Embedding 001",
		description:
			"Google's state-of-the-art text embedding model. Default output dimension 3072; supports the `dimensions` parameter to shorten output (recommended: 768, 1536, 3072).",
		family: "google",
		output: ["embedding"],
		releasedAt: new Date("2025-07-14"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-embedding-001",
				inputPrice: "0.15e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 2048,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-embedding-001",
				inputPrice: "0.15e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 2048,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
		],
	},
	{
		id: "gemini-embedding-2",
		name: "Gemini Embedding 2",
		description:
			"Google's recommended embedding model — first multimodal embedding model in the Gemini API (text-only via this endpoint today). Default output dimension 3072; supports the `dimensions` parameter (recommended: 768, 1536, 3072). Embedding space is incompatible with gemini-embedding-001.",
		family: "google",
		output: ["embedding"],
		releasedAt: new Date("2026-04-22"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-embedding-2",
				inputPrice: "0.20e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 8192,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
		],
	},
	{
		id: "text-embedding-005",
		name: "Text Embedding 005",
		description:
			"Vertex AI text embedding model optimized for English. Returns 768-dimensional vectors; supports the `dimensions` parameter (1–768) and native batching (up to 250 inputs per request).",
		family: "google",
		output: ["embedding"],
		releasedAt: new Date("2024-11-18"),
		providers: [
			{
				providerId: "google-vertex",
				externalId: "text-embedding-005",
				inputPrice: "0.025e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 2048,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
		],
	},
	{
		id: "text-embedding-004",
		name: "Text Embedding 004",
		description:
			"Vertex AI text embedding model. Returns 768-dimensional vectors; supports the `dimensions` parameter (1–768) and native batching (up to 250 inputs per request).",
		family: "google",
		output: ["embedding"],
		releasedAt: new Date("2024-05-14"),
		providers: [
			{
				providerId: "google-vertex",
				externalId: "text-embedding-004",
				inputPrice: "0.025e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 2048,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
		],
	},
	{
		id: "text-multilingual-embedding-002",
		name: "Text Multilingual Embedding 002",
		description:
			"Vertex AI multilingual text embedding model covering 100+ languages. Returns 768-dimensional vectors; supports the `dimensions` parameter (1–768) and native batching (up to 250 inputs per request).",
		family: "google",
		output: ["embedding"],
		releasedAt: new Date("2024-05-14"),
		providers: [
			{
				providerId: "google-vertex",
				externalId: "text-multilingual-embedding-002",
				inputPrice: "0.025e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 2048,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
		],
	},
	{
		id: "gemini-2.5-flash-preview-tts",
		name: "Gemini 2.5 Flash Preview TTS",
		description:
			"Gemini 2.5 Flash text-to-speech model. Generates natural, controllable single-speaker audio from text via the /v1/audio/speech endpoint.",
		family: "google",
		output: ["audio"],
		releasedAt: new Date("2025-05-20"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-flash-preview-tts",
				inputPrice: "0.5e-6",
				outputPrice: "10.0e-6",
				outputAudioPrice: "10.0e-6",
				requestPrice: "0",
				contextSize: 8000,
				maxOutput: 16000,
				streaming: false,
				tools: false,
				jsonOutput: false,
				speechGenerations: true,
				supportedVoices: GEMINI_TTS_VOICES,
			},
		],
	},
	{
		id: "gemini-2.5-pro-preview-tts",
		name: "Gemini 2.5 Pro Preview TTS",
		description:
			"Gemini 2.5 Pro text-to-speech model. Generates high-quality, controllable single-speaker audio from text via the /v1/audio/speech endpoint.",
		family: "google",
		output: ["audio"],
		releasedAt: new Date("2025-05-20"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-pro-preview-tts",
				inputPrice: "1.0e-6",
				outputPrice: "20.0e-6",
				outputAudioPrice: "20.0e-6",
				requestPrice: "0",
				contextSize: 8000,
				maxOutput: 16000,
				streaming: false,
				tools: false,
				jsonOutput: false,
				speechGenerations: true,
				supportedVoices: GEMINI_TTS_VOICES,
			},
			{
				providerId: "google-vertex",
				// GA model id on Vertex AI (the preview id is AI-Studio-only).
				externalId: "gemini-2.5-pro-tts",
				inputPrice: "1.0e-6",
				outputPrice: "20.0e-6",
				outputAudioPrice: "20.0e-6",
				requestPrice: "0",
				contextSize: 8000,
				maxOutput: 16000,
				streaming: false,
				tools: false,
				jsonOutput: false,
				speechGenerations: true,
				supportedVoices: GEMINI_TTS_VOICES,
			},
		],
	},
	{
		id: "gemini-3.1-flash-tts-preview",
		name: "Gemini 3.1 Flash TTS Preview",
		description:
			"Gemini 3.1 Flash text-to-speech model. Generates low-latency, prompt-steerable audio from text via the /v1/audio/speech endpoint.",
		family: "google",
		output: ["audio"],
		releasedAt: new Date("2026-04-15"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-3.1-flash-tts-preview",
				inputPrice: "1.0e-6",
				outputPrice: "20.0e-6",
				outputAudioPrice: "20.0e-6",
				requestPrice: "0",
				contextSize: 8000,
				maxOutput: 16000,
				streaming: false,
				tools: false,
				jsonOutput: false,
				speechGenerations: true,
				supportedVoices: GEMINI_TTS_VOICES,
			},
			{
				providerId: "google-vertex",
				externalId: "gemini-3.1-flash-tts-preview",
				inputPrice: "1.0e-6",
				outputPrice: "20.0e-6",
				outputAudioPrice: "20.0e-6",
				requestPrice: "0",
				contextSize: 8000,
				maxOutput: 16000,
				streaming: false,
				tools: false,
				jsonOutput: false,
				speechGenerations: true,
				supportedVoices: GEMINI_TTS_VOICES,
			},
		],
	},
	{
		id: "gemini-2.5-flash-native-audio-preview-12-2025",
		name: "Gemini 2.5 Flash Native Audio Preview (12-2025)",
		description:
			"Google's native-audio Gemini Live model. Served via the gateway's /v1/realtime WebSocket endpoint using Gemini's own BidiGenerateContent protocol, with text and audio input/output and function calling.",
		family: "google",
		output: ["text", "audio"],
		stability: "beta",
		releasedAt: new Date("2025-12-12"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-2.5-flash-native-audio-preview-12-2025",
				inputPrice: "0.5e-6",
				// Gemini Live does not support context caching, so no discounted
				// cached rate exists. The realtime billing path requires a cached
				// price for every priced modality, so the full rate is declared here:
				// a usage block that ever reports cached tokens is then billed at
				// full price rather than failing as unpriceable.
				cachedInputPrice: "0.5e-6",
				inputAudioPrice: "3.0e-6",
				cachedInputAudioPrice: "3.0e-6",
				outputPrice: "2.0e-6",
				outputAudioPrice: "12.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 8192,
				streaming: false,
				vision: false,
				audio: true,
				tools: true,
				jsonOutput: false,
				realtime: true,
				supportedVoices: GEMINI_TTS_VOICES,
				test: "skip",
			},
		],
	},
	{
		id: "gemini-3.1-flash-live-preview",
		name: "Gemini 3.1 Flash Live Preview",
		description:
			"Google's low-latency Gemini Live model. Served via the gateway's /v1/realtime WebSocket endpoint using Gemini's own BidiGenerateContent protocol, with text and audio input/output and function calling.",
		family: "google",
		output: ["text", "audio"],
		stability: "beta",
		releasedAt: new Date("2026-03-26"),
		providers: [
			{
				providerId: "google-ai-studio",
				externalId: "gemini-3.1-flash-live-preview",
				inputPrice: "0.75e-6",
				// Live does not support context caching, so no discounted cached rate
				// exists. The realtime billing path requires a cached price for every
				// priced modality, so the full rate is declared here: a usage block
				// that ever reports cached tokens is billed at full price rather than
				// failing as unpriceable.
				cachedInputPrice: "0.75e-6",
				inputAudioPrice: "3.0e-6",
				cachedInputAudioPrice: "3.0e-6",
				// Google prices output "including thinking tokens" at the text rate.
				outputPrice: "4.5e-6",
				outputAudioPrice: "12.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 65536,
				streaming: false,
				vision: false,
				audio: true,
				tools: true,
				jsonOutput: false,
				realtime: true,
				supportedVoices: GEMINI_TTS_VOICES,
				test: "skip",
			},
		],
	},
] as const satisfies ModelDefinition[];
